import { Baseline, IElement } from '../../../models/project.model';
import {
  getElementProperty,
  getElementPropertyResolvedCount,
  getElementPropertyResolvedCountByNameOrId,
  hasCount,
} from '../../../helpers/element-property.helpers';
import {
  AirHandlingUnitElement,
  AirHandlingUnitPropertyName,
} from './air-handling-unit.model';
import { requiredType } from '../../../helpers/function.helpers';
import { ExpressionVariables } from '../../../helpers/expression-variables.helpers';
import { getElementsInProposal } from '@shared/helpers/proposal.helpers';
import { isElementOfCategory } from '@shared/helpers/element-category.helpers';
import { ElementCategoryID } from '@shared/models/element-categories.model';
import { guards } from '@shared/helpers/typeguards.helpers';
import { clamp } from '@shared/helpers/math.helpers';

/**
 * Properties that are resolved from the air handling unit element.
 */
type ResolvedAhuProperties = {
  specificFanPower: number;
  heatRecoveryEfficiency: number;
  operationHours: number;
  postHeatingTemp?: number;
  postHeatingHours?: number;
  airflow: number;
  ventilationArea: number;
  preCoolingTemp?: number;
  preCoolingHours?: number;
};

const EMPTY_RESOLVED_AHU_PROPERTIES: ResolvedAhuProperties = {
  airflow: 0,
  ventilationArea: 0,
  specificFanPower: 0,
  heatRecoveryEfficiency: 0,
  operationHours: 0,
};

/**
 * Get the resolved properties from an air handling unit element.
 * If properties are not set it will fallback to 0 on most values
 * @param element
 * @returns
 */
const getResolvedAhuPropertiesFromElement = (
  element: IElement,
): ResolvedAhuProperties => {
  // Make sure the element is an air handling unit
  if (!isElementOfCategory(element, ElementCategoryID.AirHandlingUnit)) {
    return EMPTY_RESOLVED_AHU_PROPERTIES;
  }

  const get = (name: AirHandlingUnitPropertyName) => {
    return getElementPropertyResolvedCountByNameOrId(element, name);
  };

  const postHeating = Boolean(
    get(AirHandlingUnitPropertyName.SupplyAirPostHeating),
  );

  const ventilationParams: ResolvedAhuProperties = {
    specificFanPower: requiredType(
      get(AirHandlingUnitPropertyName.SpecificFanPower) ?? 0,
      'number',
    ),

    heatRecoveryEfficiency: requiredType(
      get(AirHandlingUnitPropertyName.HeatRecoveryEfficiency) ?? 0,
      'number',
    ),

    operationHours: requiredType(
      get(AirHandlingUnitPropertyName.OperationHours) ?? 0,
      'number',
    ),

    postHeatingTemp: postHeating
      ? requiredType(
          get(AirHandlingUnitPropertyName.PostHeatingTemperature),
          'number',
          'undefined',
        )
      : undefined,

    postHeatingHours: postHeating
      ? requiredType(
          get(AirHandlingUnitPropertyName.PostHeatingHours),
          'number',
          'undefined',
        )
      : undefined,

    airflow: requiredType(
      get(AirHandlingUnitPropertyName.Airflow) ?? 0,
      'number',
    ),

    // supplyAirFlowPerGFA: requiredType(
    //   get(AirHandlingUnitPropertyName.SupplyAirFlowPerGFA) ?? 0,
    //   'number',
    // ),

    // exhaustAirFlowPerGFA: requiredType(
    //   get(AirHandlingUnitPropertyName.ExhaustAirFlowPerGFA) ?? 0,
    //   'number',
    // ),

    ventilationArea: requiredType(
      get(AirHandlingUnitPropertyName.VentilationArea) ?? 0,
      'number',
    ),

    // preCoolingTemp: preCooling
    //   ? requiredType(
    //       get(AirHandlingUnitPropertyName.PreCoolingTemperature),
    //       'number',
    //       'undefined',
    //     )
    //   : undefined,
    // preCoolingHours: preCooling
    //   ? requiredType(
    //       get(AirHandlingUnitPropertyName.PreCoolingHours),
    //       'number',
    //       'undefined',
    //     )
    //   : undefined,
  };

  return ventilationParams;
};

const getActiveAirHandlingUnits = (
  version: Baseline,
): AirHandlingUnitElement[] =>
  getElementsInProposal(version).filter((el) =>
    isElementOfCategory(el, ElementCategoryID.AirHandlingUnit),
  );

export const getCombinedAhuPropertiesFromVersion = (
  version: Baseline,
): ResolvedAhuProperties => {
  const properties = getActiveAirHandlingUnits(version).map(
    getResolvedAhuPropertiesFromElement,
  );
  if (properties.length === 0) {
    return { ...EMPTY_RESOLVED_AHU_PROPERTIES };
  }

  return properties.reduce(
    (
      acc,
      {
        airflow,
        ventilationArea,
        postHeatingTemp,
        preCoolingTemp,
        operationHours,
        specificFanPower,
        heatRecoveryEfficiency,
      },
    ) => {
      acc.airflow += airflow; // Summarize the airflow
      acc.ventilationArea += ventilationArea; // Summarize the ventilation area
      acc.specificFanPower += specificFanPower / properties.length; // Average specific fan power
      acc.operationHours += operationHours / properties.length; // Average operation hours
      acc.heatRecoveryEfficiency += clamp(
        heatRecoveryEfficiency / properties.length,
        0,
        1,
      ); // Average heat recovery efficiency

      // Take the highest post heating temp
      if (postHeatingTemp) {
        acc.postHeatingTemp = guards.is.number(acc.postHeatingTemp)
          ? Math.max(acc.postHeatingTemp, postHeatingTemp)
          : postHeatingTemp;
      }
      // Take the lowest pre cooling temp
      if (preCoolingTemp) {
        acc.preCoolingTemp = guards.is.number(acc.preCoolingTemp)
          ? Math.min(acc.preCoolingTemp, preCoolingTemp)
          : preCoolingTemp;
      }

      return acc;
    },
    { ...EMPTY_RESOLVED_AHU_PROPERTIES },
  );
};

/**
 * To better support the user, we fallback the exhaust and supply air flow per GFA from the airflow.
 * This is done to avoid the user to have to set the exhaust and supply air flow per GFA manually.
 * This is only done if the airflow is set and the ventilation area is set.
 * @param element
 * @param variables
 * @returns
 */
export const fallbackExhaustAndSupplyAirFlowFromAirflow = (
  variables: ExpressionVariables,
  element: IElement,
) => {
  const airflowProperty = getElementProperty(
    element,
    AirHandlingUnitPropertyName.Airflow,
  );
  const supplyAir = getElementProperty(
    element,
    AirHandlingUnitPropertyName.SupplyAirFlowPerGFA,
  );
  const exhaustAir = getElementProperty(
    element,
    AirHandlingUnitPropertyName.ExhaustAirFlowPerGFA,
  );
  const area = getElementPropertyResolvedCountByNameOrId(
    element,
    AirHandlingUnitPropertyName.VentilationArea,
    0,
  );

  if (hasCount(airflowProperty) && area) {
    const airflow = getElementPropertyResolvedCount(airflowProperty, 0);
    const airflowPerArea = (1000 * airflow) / area; // From m³/s to l/m²s
    if (!hasCount(supplyAir)) {
      variables = {
        ...variables,
        [AirHandlingUnitPropertyName.SupplyAirFlowPerGFA]: airflowPerArea,
      };
    }
    if (!hasCount(exhaustAir)) {
      variables = {
        ...variables,
        [AirHandlingUnitPropertyName.ExhaustAirFlowPerGFA]: airflowPerArea,
      };
    }
  }
  return variables;
};
